import { Index } from "solid-js"
import { useLanguage } from "@/context/language"
import { Bar, Skeleton } from "./skeleton"
import type { FilesScope } from "./source-files"
import { ColumnSkeleton } from "./source-files-skeleton"
import { TablerIcon } from "./tabler-icons"
import "./tokens.css"
import "./workflows.css"

// Workflows while it loads (DESIGN-LOG §63), in the page's own boxes: Source files' column at the URL's level (the
// clients, or one scope's reports, a name and one line each: its skeleton is Source files'), the picked factsheet's head
// with its paper, its card (one quiet line until its state is known), the four cells of what the client reports are
// built from, its updates and its automatic updates' line (§68); an update opened in place, its steps down the page.
// Same classes as workflows-page.tsx and drill-column.tsx draw, so every height and gap is the page's.

type T = (key: string) => string
const useT = (): T => {
  const language = useLanguage()
  return (key) => language.t(key as Parameters<typeof language.t>[0])
}

// What the client reports are built from: "Built by the latest update" and the four numbered cells (a step, its value,
// one short line).
export function BuiltSkeleton(props: { label?: string }) {
  return (
    <Skeleton label={props.label}>
      <p class="dx-wf-sect-sub"><Bar w={170} /></p>
      <div class="dx-wf-steps">
        <Index each={[44, 52, 40, 58]}>{(width) => (
          <div class="dx-wf-step">
            <div class="dx-wf-cell">
              <span class="dx-wf-cap"><Bar round w={16} h={16} /><Bar w={`${width()}%`} /></span>
              <span class="dx-wf-val"><Bar w={`${width() - 8}%`} /></span>
              <small><Bar w="72%" /></small>
            </div>
          </div>
        )}</Index>
      </div>
    </Skeleton>
  )
}

// The picked factsheet: its head (the paper, the name, its facts' line), the card as one quiet line, the four cells, its
// updates and its automatic updates' line.
function FactsheetSkeleton(props: { label?: string }) {
  return (
    <Skeleton class="dx-wf-view" label={props.label}>
      <div class="dx-wf-head">
        <Bar class="dx-wf-thumb" w={60} h={42} />
        <div class="dx-wf-t">
          <h2><Bar w={180} /></h2>
          <p class="dx-wf-facts"><Bar w={300} /></p>
        </div>
      </div>
      <div class="dx-wf-now" data-tone="quiet">
        <Bar round />
        <div class="dx-wf-tx"><b><Bar w={210} /></b></div>
      </div>
      <section class="dx-wf-sect">
        <h3><Bar w={150} /></h3>
        <BuiltSkeleton />
      </section>
      <section class="dx-wf-sect">
        <h3><Bar w={70} /></h3>
        <table class="dx-wf-table">
          <thead><tr><Index each={[52, 34, 46, 32]}>{(width) => <th><Bar w={width()} /></th>}</Index><th class="dx-wf-end" /></tr></thead>
          <tbody>
            <tr><td><Bar w={110} /></td><td><Bar w={60} /></td><td><Bar w={84} /></td><td><Bar w={64} /></td><td class="dx-wf-end" /></tr>
          </tbody>
        </table>
      </section>
      <section class="dx-wf-auto">
        <TablerIcon name="clock" size={16} stroke-width="1.6" />
        <div class="dx-wf-tx"><Bar w={170} /></div>
        <Bar w={52} h={28} />
      </section>
    </Skeleton>
  )
}

// An update opened in place: back to its factsheet, its title and badge, its four steps down the page, what the
// assistant said.
export function UpdateSkeleton(props: { label?: string; back?: boolean }) {
  return (
    <Skeleton class="dx-wf-view dx-wf-run" label={props.label}>
      {props.back && <span class="dx-wf-back"><TablerIcon name="chevron-left" size={16} stroke-width="1.6" /><Bar w={120} /></span>}
      <div class="dx-wf-run-head">
        <div class="dx-wf-t">
          <h2><Bar w={220} /><Bar class="dx-wf-badge" h={20} /></h2>
          <p class="dx-wf-sub"><Bar w={280} /></p>
        </div>
      </div>
      <ol class="dx-wf-vsteps">
        <Index each={[38, 52, 46, 60]}>{(width) => (
          <li class="dx-wf-vstep">
            <Bar round />
            <div><b><Bar w={90} /></b><p><Bar w={`${width()}%`} /></p></div>
          </li>
        )}</Index>
      </ol>
      <div class="dx-wf-said">
        <h4><Bar w={150} /></h4>
        <p><Bar w="78%" /></p>
      </div>
    </Skeleton>
  )
}

// The page's own read while it is not in: the factsheet, or the update the URL opens.
export function WorkflowsMainSkeleton(props: { update?: boolean }) {
  const t = useT()
  return (
    <div class="dx-wf-page">
      {props.update ? <UpdateSkeleton back label={t("datox.v2.workflows.run.loading")} /> : <FactsheetSkeleton label={t("datox.v2.workflows.loading")} />}
    </div>
  )
}

// The whole page, before its code and its first read are in: the column at the URL's level (`drill`, source-files.ts
// drillScope) and the main area. `update`: the URL opens an update in place.
export function WorkflowsSkeleton(props: { update?: boolean; drill?: FilesScope["kind"] }) {
  const t = useT()
  return (
    <Skeleton class="dx-v2 dx-wf">
      <ColumnSkeleton title={t("datox.catalog.workflows")} drill={props.drill} lines={1} />
      <main class="dx-wf-main"><WorkflowsMainSkeleton update={props.update} /></main>
    </Skeleton>
  )
}
